import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Image as ImageIcon, Search, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * VariacoesEditor — gerencia lista de variações do produto.
 * Cada variação: { codigo, imagem, preco (opcional, fallback = preço do produto) }.
 * Usado no modal de editar/criar produto do admin (Products.js).
 */
export default function VariacoesEditor({ variacoes = [], onChange }) {
  const [filter, setFilter] = useState('');
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);

  const update = (idx, patch) => {
    const next = variacoes.map((v, i) => i === idx ? { ...v, ...patch } : v);
    onChange(next);
  };

  const remove = (idx) => {
    if (!window.confirm(`Remover variação "${variacoes[idx].codigo}"?`)) return;
    onChange(variacoes.filter((_, i) => i !== idx));
  };

  const addNew = () => {
    const nextCode = `NOVA-${(variacoes.length + 1).toString().padStart(2, '0')}`;
    onChange([...variacoes, { codigo: nextCode, imagem: '', preco: null, tipo: 'modelo', descricao: '' }]);
  };

  const sortByCodigo = () => {
    const sorted = [...variacoes].sort((a, b) => {
      const na = (a.codigo || '').match(/([A-Z]+)-?(\d+)/i);
      const nb = (b.codigo || '').match(/([A-Z]+)-?(\d+)/i);
      if (na && nb) {
        if (na[1] !== nb[1]) return na[1].localeCompare(nb[1]);
        return parseInt(na[2], 10) - parseInt(nb[2], 10);
      }
      return (a.codigo || '').localeCompare(b.codigo || '');
    });
    onChange(sorted);
  };

  const uploadImage = async (idx, file) => {
    setUploadingIdx(idx);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/upload/image/products`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update(idx, { imagem: resp.data.url });
      toast.success('Imagem enviada');
    } catch (e) {
      toast.error('Erro no upload: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploadingIdx(null);
    }
  };

  // Upload em lote: cria uma nova variação por arquivo, usando o nome do arquivo (sem extensão) como código
  const bulkUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBulkUploading(true);
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const resp = await axios.post(`${API}/upload/image/products`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const codigo = file.name.replace(/\.[^.]+$/, '').toUpperCase();
        added.push({ codigo, imagem: resp.data.url, preco: null, tipo: 'modelo', descricao: '' });
      } catch (e) {
        toast.error(`Erro em ${file.name}`);
      }
    }
    onChange([...variacoes, ...added]);
    toast.success(`${added.length} variações adicionadas`);
    setBulkUploading(false);
  };

  const filtered = filter
    ? variacoes
        .map((v, i) => ({ v, i }))
        .filter(({ v }) => (v.codigo || '').toLowerCase().includes(filter.toLowerCase()))
    : variacoes.map((v, i) => ({ v, i }));

  return (
    <div className="space-y-3" data-testid="variacoes-editor">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Buscar entre ${variacoes.length} variações…`}
              className="w-full pl-8 pr-3 py-1.5 rounded-md border border-border bg-background text-sm"
              data-testid="variacoes-search"
            />
          </div>
          <button type="button" onClick={sortByCodigo} className="p-1.5 rounded-md border border-border hover:bg-accent" title="Ordenar A→Z" data-testid="variacoes-sort">
            <ArrowUpDown className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => bulkInputRef.current?.click()}
            disabled={bulkUploading}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"
            data-testid="variacoes-bulk-upload"
          >
            <Upload className="w-3.5 h-3.5" />
            {bulkUploading ? 'Enviando...' : 'Upload em Lote'}
          </button>
          <input
            ref={bulkInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => bulkUpload(Array.from(e.target.files || []))}
          />
          <button
            type="button"
            onClick={addNew}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-semibold"
            data-testid="variacoes-add"
          >
            <Plus className="w-3.5 h-3.5" />
            Nova Variação
          </button>
        </div>
      </div>

      {variacoes.length === 0 && (
        <div className="text-center py-10 border-2 border-dashed border-border rounded-lg">
          <ImageIcon className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground mb-3">Nenhuma variação. Adicione uma manualmente ou faça upload em lote (o nome do arquivo vira o código).</p>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="max-h-[380px] overflow-y-auto divide-y divide-border">
            {filtered.map(({ v, i }) => (
              <div key={i} className="p-2 hover:bg-accent/30 space-y-1.5" data-testid={`variacao-row-${v.codigo}`}>
                <div className="flex items-center gap-3">
                  {/* Preview */}
                  <div className="w-14 h-14 rounded border border-border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                    {v.imagem ? (
                      <img src={v.imagem} alt={v.codigo} className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                  {/* Tipo */}
                  <div className="w-24">
                    <label className="text-[10px] uppercase font-semibold text-muted-foreground">Tipo</label>
                    <select
                      value={v.tipo || 'modelo'}
                      onChange={(e) => update(i, { tipo: e.target.value })}
                      className="w-full px-2 py-1 rounded border border-border bg-background text-sm"
                      data-testid={`variacao-tipo-${i}`}
                    >
                      <option value="modelo">Modelo</option>
                      <option value="cor">Cor</option>
                      <option value="estilo">Estilo</option>
                      <option value="tamanho">Tamanho</option>
                      <option value="material">Material</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  {/* Código */}
                  <div className="w-28">
                    <label className="text-[10px] uppercase font-semibold text-muted-foreground">Código</label>
                    <input
                      type="text"
                      value={v.codigo || ''}
                      onChange={(e) => update(i, { codigo: e.target.value.toUpperCase() })}
                      className="w-full px-2 py-1 rounded border border-border bg-background text-sm font-mono"
                      data-testid={`variacao-codigo-${i}`}
                    />
                  </div>
                  {/* Preço opcional */}
                  <div className="w-24">
                    <label className="text-[10px] uppercase font-semibold text-muted-foreground">Preço</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={v.preco ?? ''}
                      onChange={(e) => update(i, { preco: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      placeholder="padrão"
                      className="w-full px-2 py-1 rounded border border-border bg-background text-sm"
                      data-testid={`variacao-preco-${i}`}
                    />
                  </div>
                  {/* Ações */}
                  <div className="flex items-center gap-1 ml-auto">
                    <label className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-accent cursor-pointer text-xs font-semibold" title="Trocar imagem">
                      <Upload className="w-3 h-3" />
                      {uploadingIdx === i ? '...' : 'Foto'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && uploadImage(i, e.target.files[0])}
                        data-testid={`variacao-upload-${i}`}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => remove(i)}
                      className="p-1.5 rounded hover:bg-red-100 text-red-600"
                      title="Remover variação"
                      data-testid={`variacao-remove-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {/* Descrição / texto de referência */}
                <input
                  type="text"
                  value={v.descricao || ''}
                  onChange={(e) => update(i, { descricao: e.target.value })}
                  placeholder="Texto de referência (ex.: 'Coração vermelho grande', 'Rosa metalizado 3,5cm'…)"
                  className="w-full px-2 py-1 rounded border border-border bg-background text-xs italic"
                  data-testid={`variacao-descricao-${i}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground italic">
        Deixe o preço em branco para usar o preço padrão do produto. O código da variação aparece no popup do cliente/vendedor.
      </p>
    </div>
  );
}
